/**
 * Lift2Fit — admin: Offers (`#/admin/offers`).
 *
 * Discount codes and offers: a code, a description, a percentage or fixed
 * amount, the catalog plan it applies to, a validity window, a usage cap and a
 * live count, and an active state. Creating, editing and deactivating are all
 * deliberate, audited writes. Nothing here redeems an offer or touches a ledger
 * row.
 */
import { useEffect, useState } from 'react';
import {
  api,
  type AdminAuditEntry,
  type AdminOfferCreate,
  type OfferDiscountKind,
  type OfferView,
  type PlanCatalogEntry,
} from '@/lib/api';
import { formatDate } from '@/lib/format';
import { AlertIcon, TrendUpIcon } from '@/lib/icons';
import {
  AppLinkButton,
  EmptyState,
  LoadingPanel,
  LiveMessage,
  Panel,
  ScreenHeader,
  StatusChip,
} from '../components';
import { formatDateShort, formatMoney } from '../crm/shared';
import { useAsync } from '../hooks';
import { ADMIN_EYEBROW, formatCount } from './shared';

function discountLabel(offer: OfferView): string {
  return offer.discountKind === 'percent'
    ? `${offer.discountValue}% off`
    : `${formatMoney(offer.discountValue, 'GBP')} off`;
}

function windowLabel(offer: OfferView): string {
  if (offer.startsAt === null && offer.endsAt === null) return 'Always';
  const from = offer.startsAt === null ? 'now' : formatDateShort(offer.startsAt);
  const to = offer.endsAt === null ? 'open' : formatDateShort(offer.endsAt);
  return `${from} → ${to}`;
}

export function AdminOffersScreen() {
  const state = useAsync(() => api.adminOffers(), []);
  const plansState = useAsync(() => api.adminPlans(), []);
  const [offers, setOffers] = useState<OfferView[]>([]);
  const [audit, setAudit] = useState<AdminAuditEntry | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [discountKind, setDiscountKind] = useState<OfferDiscountKind>('percent');
  const [discountValue, setDiscountValue] = useState('10');
  const [planId, setPlanId] = useState('');
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [usageCap, setUsageCap] = useState('');
  const [createBusy, setCreateBusy] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  useEffect(() => {
    if (state.data) setOffers(state.data.data);
  }, [state.data]);

  const plans: PlanCatalogEntry[] = plansState.data?.data ?? [];

  const toggleActive = async (offer: OfferView) => {
    setBusyId(offer.offerId);
    setNotice(null);
    const result = await api.adminUpdateOffer(offer.offerId, { active: !offer.active });
    setBusyId(null);
    if (result.problem || !result.data) {
      setNotice(result.problem?.detail ?? result.problem?.title ?? 'The offer did not change.');
      return;
    }
    setOffers((current) => current.map((entry) => (entry.offerId === offer.offerId ? result.data!.offer : entry)));
    setAudit(result.data.audit);
    setNotice(`${offer.code} is now ${result.data.offer.active ? 'active' : 'inactive'}.`);
  };

  const create = async () => {
    setCreateBusy(true);
    setCreateError(null);
    const value = Number(discountValue);
    if (code.trim() === '' || !Number.isFinite(value) || value <= 0) {
      setCreateError('A code and a positive discount are required.');
      setCreateBusy(false);
      return;
    }
    if (discountKind === 'percent' && value > 100) {
      setCreateError('A percentage discount cannot exceed 100.');
      setCreateBusy(false);
      return;
    }
    const cap = usageCap.trim() === '' ? null : Number(usageCap);
    const payload: AdminOfferCreate = {
      code: code.trim(),
      description: description.trim(),
      discountKind,
      discountValue: value,
      planId: planId === '' ? null : planId,
      startsAt: startsAt.trim() === '' ? null : `${startsAt.trim()}T00:00:00.000Z`,
      endsAt: endsAt.trim() === '' ? null : `${endsAt.trim()}T00:00:00.000Z`,
      usageCap: cap !== null && Number.isInteger(cap) && cap > 0 ? cap : null,
      active: true,
    };
    const result = await api.adminCreateOffer(payload);
    setCreateBusy(false);
    if (result.problem || !result.data) {
      setCreateError(result.problem?.detail ?? result.problem?.title ?? 'The offer was not created.');
      return;
    }
    setOffers((current) => [result.data!.offer, ...current]);
    setAudit(result.data.audit);
    setNotice(`Created ${result.data.offer.code}.`);
    setCode('');
    setDescription('');
  };

  if (state.loading && offers.length === 0) {
    return <LoadingPanel label="Loading offers" rows={5} />;
  }

  if (state.error && offers.length === 0) {
    return (
      <EmptyState
        title="The offers list did not load"
        body={state.error}
        icon={<AlertIcon className="h-8 w-8 text-accent" />}
        action={
          <AppLinkButton to="/admin" variant="outline">
            Back to the overview
          </AppLinkButton>
        }
      />
    );
  }

  return (
    <div className="flex flex-col gap-6" data-testid="admin-offers">
      <ScreenHeader
        eyebrow={ADMIN_EYEBROW}
        title="Offers"
        badge={<StatusChip tone="danger">Founder only</StatusChip>}
        lead="Discount codes: percentage or fixed amount, the plan they apply to, a validity window and a usage cap. Create, edit and deactivate here; redemption is a later, deliberate integration."
        actions={
          <AppLinkButton to="/admin/plans" variant="outline">
            <TrendUpIcon className="text-[14px]" />
            Plans & pricing
          </AppLinkButton>
        }
      />

      {notice ? <LiveMessage message={notice} tone="success" /> : null}

      <Panel title="Create an offer" subtitle="The code is stored upper-case; every change writes an audit row." testId="admin-offer-new">
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1">
            <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">Code</span>
            <input
              data-testid="admin-offer-new-code"
              value={code}
              placeholder="LAUNCH20"
              onChange={(event) => setCode(event.target.value)}
              className="h-9 w-32 border border-hairline bg-page px-2 font-mono text-[13px] uppercase text-ink"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">Description</span>
            <input
              data-testid="admin-offer-new-description"
              value={description}
              placeholder="Launch week"
              onChange={(event) => setDescription(event.target.value)}
              className="h-9 w-48 border border-hairline bg-page px-2 text-[13px] text-ink"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">Kind</span>
            <select
              data-testid="admin-offer-new-kind"
              value={discountKind}
              onChange={(event) => setDiscountKind(event.target.value as OfferDiscountKind)}
              className="h-9 border border-hairline bg-page px-2 text-[13px] text-ink"
            >
              <option value="percent">Percent</option>
              <option value="amount">Fixed amount</option>
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">Value</span>
            <input
              data-testid="admin-offer-new-value"
              value={discountValue}
              inputMode="decimal"
              onChange={(event) => setDiscountValue(event.target.value)}
              className="l2f-tabular h-9 w-20 border border-hairline bg-page px-2 font-mono text-[13px] text-ink"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">Plan</span>
            <select
              data-testid="admin-offer-new-plan"
              value={planId}
              onChange={(event) => setPlanId(event.target.value)}
              className="h-9 border border-hairline bg-page px-2 text-[13px] text-ink"
            >
              <option value="">Any plan</option>
              {plans.map((plan) => (
                <option key={plan.planId} value={plan.planId}>
                  {plan.audience} · {plan.displayName}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">Starts</span>
            <input
              data-testid="admin-offer-new-starts"
              type="date"
              value={startsAt}
              onChange={(event) => setStartsAt(event.target.value)}
              className="h-9 border border-hairline bg-page px-2 text-[13px] text-ink"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">Ends</span>
            <input
              data-testid="admin-offer-new-ends"
              type="date"
              value={endsAt}
              onChange={(event) => setEndsAt(event.target.value)}
              className="h-9 border border-hairline bg-page px-2 text-[13px] text-ink"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">Usage cap</span>
            <input
              data-testid="admin-offer-new-cap"
              value={usageCap}
              inputMode="numeric"
              placeholder="unlimited"
              onChange={(event) => setUsageCap(event.target.value)}
              className="l2f-tabular h-9 w-24 border border-hairline bg-page px-2 font-mono text-[13px] text-ink"
            />
          </label>
          <button
            type="button"
            data-testid="admin-offer-new-create"
            disabled={createBusy}
            onClick={() => void create()}
            className="inline-flex h-9 items-center bg-accent px-4 text-[11px] font-bold uppercase tracking-[0.08em] text-on-accent hover:bg-accent-hover disabled:opacity-40"
          >
            {createBusy ? 'Creating…' : 'Create offer'}
          </button>
        </div>
        {createError ? (
          <p role="alert" data-testid="admin-offer-new-error" className="text-[12px] text-danger">
            {createError}
          </p>
        ) : null}
      </Panel>

      <Panel title="All offers" subtitle="Newest first. Deactivate keeps the row and its audit trail." testId="admin-offers-list">
        <p className="text-[12px] text-ink-muted" data-testid="admin-offers-total">
          {formatCount(offers.length)} offer(s)
        </p>
        {offers.length === 0 ? (
          <EmptyState
            title="No offers yet"
            body="Create the first discount code above. It will be audited and listed here."
            icon={<TrendUpIcon className="h-8 w-8 text-accent" />}
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[880px] border-collapse text-left">
              <thead>
                <tr className="border-b border-hairline text-[10px] uppercase tracking-[0.1em] text-ink-muted">
                  <th className="py-2 pr-3 font-bold">Code</th>
                  <th className="py-2 pr-3 font-bold">Discount</th>
                  <th className="py-2 pr-3 font-bold">Plan</th>
                  <th className="py-2 pr-3 font-bold">Window</th>
                  <th className="py-2 pr-3 font-bold">Usage</th>
                  <th className="py-2 pr-3 font-bold">State</th>
                  <th className="py-2 font-bold">Action</th>
                </tr>
              </thead>
              <tbody>
                {offers.map((offer) => (
                  <tr
                    key={offer.offerId}
                    data-testid={`admin-offer-row-${offer.offerId}`}
                    className="border-b border-hairline last:border-b-0 align-top"
                  >
                    <td className="py-3 pr-3">
                      <span className="flex min-w-0 flex-col">
                        <span className="font-mono text-[13px] font-bold text-ink">{offer.code}</span>
                        <span className="text-[12px] text-ink-muted">{offer.description || '—'}</span>
                      </span>
                    </td>
                    <td className="py-3 pr-3 text-[12px] text-ink">{discountLabel(offer)}</td>
                    <td className="py-3 pr-3 text-[12px] text-ink-muted">
                      {offer.planName === null ? 'Any plan' : `${offer.audience ?? ''} · ${offer.planName}`}
                    </td>
                    <td className="py-3 pr-3 text-[12px] text-ink-muted">{windowLabel(offer)}</td>
                    <td className="l2f-tabular py-3 pr-3 font-mono text-[12px] text-ink-muted">
                      {formatCount(offer.usageCount)} / {offer.usageCap === null ? '∞' : formatCount(offer.usageCap)}
                    </td>
                    <td className="py-3 pr-3" data-testid={`admin-offer-state-${offer.offerId}`}>
                      {offer.active ? (
                        <StatusChip tone={offer.redeemable ? 'success' : 'warning'}>
                          {offer.redeemable ? 'Redeemable' : 'Active · out of window'}
                        </StatusChip>
                      ) : (
                        <StatusChip tone="neutral">Inactive</StatusChip>
                      )}
                    </td>
                    <td className="py-3">
                      <button
                        type="button"
                        data-testid={`admin-offer-toggle-${offer.offerId}`}
                        disabled={busyId === offer.offerId}
                        onClick={() => void toggleActive(offer)}
                        className="inline-flex h-9 items-center border border-hairline px-3 text-[11px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent hover:text-accent disabled:opacity-40"
                      >
                        {busyId === offer.offerId ? 'Writing…' : offer.active ? 'Deactivate' : 'Activate'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {audit ? (
        <p
          className="border-l-2 border-accent bg-page-subtle px-3 py-2 text-[12px] text-ink-muted"
          data-testid="admin-offers-last-audit"
        >
          Audited: <span className="font-bold text-ink">{audit.summary}</span> · {audit.actorEmail ?? 'admin'} ·{' '}
          {formatDate(audit.createdAt)}
        </p>
      ) : null}
    </div>
  );
}
